import { useMemo, useRef } from 'react';
import { Flex, Pane } from '@leverege/ui-elements';
import { Relationships } from '@leverege/ui-attributes';
import { LoadingView } from '@leverege/molten/views';
import { TimelineProvider } from "./TimelineContext.js";
import ImagineRsrcGroupDelegate from "../../analyzer/ImagineRsrcGroupDelegate.js";
import ResourceTimelineViewer from "./viewers/resource/ResourceTimelineViewer.js";
import SelectedDialog from "../../components/rsrcGallery/SelectedDialog.js";
import TimelineModel from "./TimelineModel.js";
import TimelineScrubber from "./TimelineScrubber.js";
import useDateTimeRange from "./hooks/useDateTimeRange.js";
import useLoadPaths from "./hooks/useLoadPaths.js";
import useObserveResize from "./hooks/useObserveResize.js";
import usePaginationControls from "../../components/rsrcGallery/hooks/usePaginationControls.js";
import useThumbnailSelection from "../../components/rsrcGallery/hooks/useThumbnailSelection.js";
import useTick from "./hooks/useTick.js";
import useTimelineAxis from "./hooks/useTimelineAxis.js";
import useTimelineManager from "./hooks/useTimelineManager.js";
import S from './TimelineView.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const PADDING_X = 16;
const PADDING_Y = 16;
const ROW_GAP = 16;
const COLUMN_GAP = 16;
export default function TimelineView(props) {
  const {
    delegate,
    paginator,
    path,
    relationship,
    selectionKey,
    settings,
    reloadData
  } = props;
  const {
    range,
    onChangeRange,
    now,
    onChangeNow
  } = useDateTimeRange({
    settings
  });
  const {
    ticks
  } = useTimelineAxis({
    range
  });
  const {
    addTickCallback,
    isPlaying,
    onJumpToNow,
    onPlayPause,
    onTick: onChangeScrubTime,
    removeTickCallback,
    scrubTime
  } = useTick({
    now,
    onChangeNow,
    onChangeRange,
    range,
    tickInterval: TimelineModel.getTickInterval(settings),
    ticks,
    tickWindowDuration: TimelineModel.getTickWindowDuration(settings)
  });
  const {
    addDataset,
    error,
    loading,
    state: nearestData
  } = useTimelineManager({
    addTickCallback,
    removeTickCallback,
    precision: TimelineModel.getPrecision(settings),
    scrubTime
  });
  const rsrcDelegate = useMemo(() => {
    const rsrcRel = Relationships.getByPath(`${delegate.relationship.path}.rsrc`);
    return new ImagineRsrcGroupDelegate({
      match: delegate.match,
      relationship: rsrcRel.createResolved(delegate.match, {
        parent: delegate.relationship
      }),
      queryName: `${delegate.id}.rsrc.timeline`
    });
  }, [delegate.id]);
  const {
    selectedItem,
    onSelect,
    onClose
  } = useThumbnailSelection({
    selectionKey,
    delegate: rsrcDelegate
  });
  const timelineContext = useMemo(() => {
    return {
      addDataset,
      delegate,
      error,
      loading,
      nearestData,
      path,
      pathPrefix: settings?.pathPrefix,
      range,
      relationship,
      rsrcDelegate,
      scrubTime,
      selectionKey,
      onThumbnailSelection: onSelect,
      onDetailClose: onClose
    };
  }, [addDataset, delegate, error, loading, nearestData, onClose, onSelect, path, range, relationship, rsrcDelegate, scrubTime, selectionKey, settings?.pathPrefix]);
  const {
    paths,
    loading: pathsLoading
  } = useLoadPaths({
    rsrcDelegate,
    pathPrefix: settings?.pathPrefix,
    range
  });
  const containerRef = useRef();
  const {
    height,
    width
  } = useObserveResize(containerRef);
  const {
    rows,
    cols
  } = useMemo(() => {
    if (width === 0 || height === 0) {
      return {
        rows: 1,
        cols: 1
      };
    }
    const w = width - PADDING_X * 2;
    const h = height - PADDING_Y * 2;
    let bestCols = 1;
    let bestRows = paths.length;
    let maxCellSize = 0;
    for (let cols = 1; cols <= paths.length; cols++) {
      const rows = Math.ceil(paths.length / cols);
      const totalGapX = (cols - 1) * COLUMN_GAP;
      const totalGapY = (rows - 1) * ROW_GAP;
      const cellWidth = (w - totalGapX) / cols;
      const cellHeight = (h - totalGapY) / rows;
      const cellSize = Math.min(cellWidth, cellHeight);
      if (cellSize > maxCellSize) {
        maxCellSize = cellSize;
        bestCols = cols;
        bestRows = rows;
      }
    }
    return {
      rows: bestRows,
      cols: bestCols
    };
  }, [height, width, paths.length]);
  const {
    selectedIndex,
    onNextItem,
    onPreviousItem,
    hasCycled,
    cannotCycle
  } = usePaginationControls({
    paginator,
    onSelect,
    selectedItem
  });
  return /*#__PURE__*/_jsxs(TimelineProvider, {
    value: timelineContext,
    children: [/*#__PURE__*/_jsxs(Pane, {
      domRef: containerRef,
      style: {
        '--numPaths': paths.length,
        '--num-rows': rows,
        '--num-cols': cols
      },
      className: S.timelineView,
      variant: "timelineView",
      children: [pathsLoading ? /*#__PURE__*/_jsx(Flex, {
        className: S.loading,
        variant: "colMCenterMiddle",
        children: /*#__PURE__*/_jsx(LoadingView, {
          size: 80
        })
      }) : /*#__PURE__*/_jsx("div", {
        className: S.sizingParentShim,
        children: /*#__PURE__*/_jsx(Pane, {
          className: S.timelineViewContent,
          variant: "timelineViewContent",
          children: paths?.map(path => /*#__PURE__*/_jsx(ResourceTimelineViewer, {
            path: path,
            settings: settings
          }, path.name))
        })
      }), /*#__PURE__*/_jsx(TimelineScrubber, {
        addDataset: addDataset,
        clientProps: props,
        isPlaying: isPlaying,
        loading: loading,
        onChangeRange: onChangeRange,
        onChangeScrubTime: onChangeScrubTime,
        onPlayPause: onPlayPause,
        range: range,
        scrubTime: scrubTime,
        ticks: ticks,
        onJumpToNow: onJumpToNow
      })]
    }), selectedItem && /*#__PURE__*/_jsx(SelectedDialog, {
      ...props,
      selectedIndex: selectedIndex,
      path: path,
      show: selectedItem,
      onClose: onClose,
      settings: settings,
      selectionKey: selectionKey,
      paginator: paginator,
      delegate: rsrcDelegate,
      reloadData: reloadData,
      onNextItem: onNextItem,
      onPreviousItem: onPreviousItem,
      hasCycled: hasCycled,
      cannotCycle: cannotCycle
    })]
  });
}